import { useQuery } from '@tanstack/react-query'
import { CmApiHttpError, cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { useBookmakerSessionFlag } from '@/api/hooks/useCmMe'
import {
  dayEndIso,
  dayStartIso,
  omitAllFilter,
  type MemberBetSource,
  type MemberBetTime,
  type MemberBetType,
  type MemberSportBookBet,
} from '@/api/map/memberBets'
import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import { ROLE_LABEL, type Role } from '@/features/hierarchy/hierarchy'

export type MemberBetsQuery = {
  accountId: string
  accountRole: Role
  source: MemberBetSource
  fromDate: string
  toDate: string
  time: MemberBetTime
  betType: MemberBetType
  page: number
  pageSize: number
  game?: string
  subGame?: string
  eventName?: string
  marketName?: string
  marketUniqueId?: string
  marketType?: string
  stakeFrom?: number
  stakeTo?: number
  memberUsername?: string | null
  enabled?: boolean
  /** Staff Market access — omit / `all` skips the wire field. */
  bookmakerSessionFlag?: BookmakerSessionFlag
}

/** @deprecated Prefer MemberSportBookBet — kept for older call sites/tests. */
export type MemberBetRow = MemberSportBookBet

type Paged = { rows: MemberSportBookBet[]; total: number; page: number; pageSize: number }

function empty(query: MemberBetsQuery): Paged {
  return { rows: [], total: 0, page: query.page, pageSize: query.pageSize }
}

function wireSource(source: MemberBetsQuery['source']): 'sportsbook' | 'premium-bm' {
  return source === 'premium-bm' ? 'premium-bm' : 'sportsbook'
}

function toBet(r: Record<string, unknown>, page: number, i: number): MemberSportBookBet {
  const marketName = String(r.marketName ?? '')
  const betType = String(r.betType ?? '')
  const isLineLadder =
    betType.toLowerCase() === 'lineladder' ||
    /line\s*session|overs?\s*line|ball\s*line/i.test(marketName)
  const sideRaw = String(r.type ?? r.side ?? '')
  return {
    id: String(r.betId ?? r._id ?? r.id ?? `${page}-${i}`),
    memberId: String(r.memberId ?? ''),
    userName: String(r.userName ?? r.username ?? ''),
    placedAt: r.placedDate != null ? String(r.placedDate) : r.placedAt != null ? String(r.placedAt) : null,
    matchedAt:
      r.matchedTime != null
        ? String(r.matchedTime)
        : r.matchedAt != null
          ? String(r.matchedAt)
          : null,
    eventName: String(r.event ?? r.eventName ?? ''),
    eventType: String(r.eventType ?? ''),
    marketId: String(r.marketId ?? ''),
    marketName,
    selection: String(r.selectionName ?? r.selection ?? ''),
    side: sideRaw.toUpperCase() === 'LAY' ? 'lay' : 'back',
    oddsPlaced: Number(r.betRate ?? r.oddsPlaced ?? 0),
    oddsMatched: Number(r.averagePriceMatched ?? r.oddValue ?? r.oddsMatched ?? 0),
    runs: r.runs == null || r.runs === '' ? null : Number(r.runs),
    stake: Number(r.stake ?? 0),
    matched: Number(r.matchedAmt ?? r.matchedSize ?? r.matched ?? 0),
    unmatched: Number(r.unMatchedAmt ?? r.sizeRemaining ?? r.unmatched ?? 0),
    status: String(r.betStatus ?? r.status ?? ''),
    betWinStatus: String(r.betWinStatus ?? ''),
    ip: String(r.IP ?? r.ip ?? ''),
    currencyType: String(r.currencyType ?? 'INR'),
    betType,
    isBookmaker:
      Boolean(r.isBookmaker) || marketName.toLowerCase().includes('bookmaker'),
    isLineLadder,
  }
}

/**
 * Map getMemberBets payload after CM-API Standard `{ data }` peel.
 * Accepts Lab `results` (betId / matchedAmt / …) plus nested `data` shapes.
 */
export function memberBetsFromLive(
  live: Record<string, unknown>,
  page: number,
  pageSize: number,
): Paged {
  const nested = live.data
  const fromNested = Array.isArray(nested)
    ? nested
    : nested && typeof nested === 'object' && Array.isArray((nested as { data?: unknown }).data)
      ? (nested as { data: unknown[] }).data
      : null
  const results = (
    Array.isArray(live.results)
      ? live.results
      : (fromNested ?? (Array.isArray(live.data) ? live.data : []))
  ) as Record<string, unknown>[]
  return {
    rows: results.map((r, i) => toBet(r, page, i)),
    total: Number(live.count ?? live.total ?? results.length),
    page,
    pageSize,
  }
}

/** Wire body for `POST /v1/bets/getMemberBets` (filter omit + Past/Premium rules). */
export function buildMemberBetsRequestBody(
  query: MemberBetsQuery,
): Record<string, unknown> {
  const isPremium = query.source === 'premium-bm'
  const game = omitAllFilter(query.game)
  const subGame = omitAllFilter(query.subGame)
  const eventName = omitAllFilter(query.eventName)
  const marketName = omitAllFilter(query.marketName)
  const marketType = omitAllFilter(query.marketType)
  return {
    userId: query.accountId,
    accessLevel: ROLE_LABEL[query.accountRole],
    fromDate: dayStartIso(query.fromDate),
    toDate: dayEndIso(query.toDate),
    time: query.time,
    betType: isPremium || query.time === 'Past' ? 'Matched' : query.betType,
    previousData: isPremium ? query.time === 'Past' : undefined,
    source: wireSource(query.source),
    page: query.page,
    maxrow: query.pageSize,
    ...(game ? { game } : {}),
    ...(subGame ? { subGame } : {}),
    ...(eventName ? { eventName } : {}),
    ...(marketName ? { marketName } : {}),
    ...(query.marketUniqueId?.trim()
      ? { marketUniqueId: query.marketUniqueId.trim() }
      : {}),
    ...(marketType ? { marketType } : {}),
    ...(query.stakeFrom != null && Number.isFinite(query.stakeFrom)
      ? { stakeFrom: query.stakeFrom }
      : {}),
    ...(query.stakeTo != null && Number.isFinite(query.stakeTo)
      ? { stakeTo: query.stakeTo }
      : {}),
    ...(query.memberUsername
      ? { name: { username: query.memberUsername } }
      : {}),
    ...(query.bookmakerSessionFlag && query.bookmakerSessionFlag !== 'all'
      ? { bookmakerSessionFlag: query.bookmakerSessionFlag }
      : {}),
  }
}

async function load(query: MemberBetsQuery): Promise<Paged> {
  try {
    const live = await cmApi.getMemberBets(
      authHeaders(),
      buildMemberBetsRequestBody(query),
    )
    return memberBetsFromLive(live, query.page, query.pageSize)
  } catch (err) {
    if (err instanceof CmApiHttpError && (err.status === 404 || err.status === 503)) {
      return empty(query)
    }
    return empty(query)
  }
}

export function useMemberBets(query: MemberBetsQuery) {
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  const withFlag: MemberBetsQuery = {
    ...query,
    bookmakerSessionFlag: query.bookmakerSessionFlag ?? bookmakerSessionFlag,
  }
  return useQuery({
    queryKey: ['cm', 'member-bets', withFlag],
    queryFn: () => load(withFlag),
    enabled: Boolean(query.accountId && (query.enabled ?? true)),
    placeholderData: (prev) => prev,
  })
}
